import React, { useEffect } from "react";
import {
  Box,
  Typography,
  Grid,
  Button,
  Paper,
  Stack,
  Avatar,
  Rating,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ScheduleIcon from "@mui/icons-material/Schedule";
import BoltIcon from "@mui/icons-material/Bolt";
import ForumIcon from "@mui/icons-material/Forum";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import DeviceHubIcon from "@mui/icons-material/DeviceHub";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import FormatQuoteRoundedIcon from "@mui/icons-material/FormatQuoteRounded";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import HeroSection from "../components/HeroSection";
import newsDeskImage from '../assets/newsdesk_screendump.png';
import AdCard from "../components/AdCard";
import SEO from "../components/SEO";
import { useTranslation } from "react-i18next";

const PLAN_ORDER = ['basic', 'pro', 'elite'];

const PLAN_STYLES = {
  basic: {
    accent: '#2b7bff',
    background: 'linear-gradient(180deg, rgba(19,36,73,0.85) 0%, rgba(10,20,53,0.95) 100%)',
  },
  pro: {
    accent: '#f7b633',
    background: 'linear-gradient(180deg, rgba(73,52,12,0.9) 0%, rgba(41,28,6,0.95) 100%)',
  },
  elite: {
    accent: '#f0625f',
    background: 'linear-gradient(180deg, rgba(73,19,23,0.9) 0%, rgba(40,9,12,0.96) 100%)',
  },
};

const DEFAULT_PLANS = {
  basic: {
    name: 'BASIC',
    priceAmount: '$14.99',
    pricePeriod: '/month',
    features: [
      'Sentiment analysis from multiple brokers',
      'Live NewsDesk with real-time headlines',
      'Trading Floor community access',
      'Week Ahead – earnings & market direction',
    ],
  },
  pro: {
    name: 'PRO',
    priceAmount: '$29.99',
    pricePeriod: '/month',
    features: [
      'Everything in Basic',
      'Flow Calls 2–3x per week – live analysis',
      'Option Flow, Breakdown & Deep Dive',
      'Full market intelligence & mentor feedback',
    ],
  },
  elite: {
    name: 'ELITE',
    priceAmount: '$89.99',
    pricePeriod: '/month',
    features: [
      'Everything in Pro',
      '1:1 coaching session (1 hour/month)',
      'Priority support & early feature access',
    ],
  },
};

const REVIEW_SHOWCASE_DEFAULTS = {
  howItWorks: {
    title: 'How FlockTrade powers your trading routine',
    subtitle: 'Three simple steps to go from curious trader to confident execution.',
    steps: [
      {
        label: 'STEP 1',
        title: 'Join the trading floor',
        description: 'Pick your membership, connect Discord, and unlock mentor access in minutes.',
        accent: '#9775fa',
        background: 'linear-gradient(180deg, rgba(46,32,89,0.85) 0%, rgba(27,16,73,0.95) 100%)',
        Icon: DeviceHubIcon,
      },
      {
        label: 'STEP 2',
        title: 'Stream market intelligence',
        description: 'Follow live orderflow analysis, NewsDesk coverage, and daily market breakdowns tailored to indices and FX.',
        accent: '#38bdf8',
        background: 'linear-gradient(180deg, rgba(14,64,90,0.85) 0%, rgba(8,34,51,0.95) 100%)',
        Icon: CloudUploadIcon,
      },
      {
        label: 'STEP 3',
        title: 'Execute with confidence',
        description: 'Put the plan into action with clearly defined entries, targets, and risk levels updated in real time.',
        accent: '#f472b6',
        background: 'linear-gradient(180deg, rgba(88,26,71,0.85) 0%, rgba(55,13,47,0.95) 100%)',
        Icon: AutorenewIcon,
      },
    ],
  },
  reviews: {
    title: 'Trusted by intraday and swing traders',
    subtitle: 'Real feedback from verified Discord members',
    cards: [
      {
        initials: 'DM',
        name: 'DarkMatter_Trades',
        role: 'Verified Discord Member · Pro since 2024',
        quote: 'The Option Flow dashboard changed how I approach my NAS100 setups. Being able to see institutional orderflow before London open gives me an edge I never had.',
        result: 'Pro member for 8+ months',
        rating: 5,
        avatarColor: '#5865F2',
        accent: '#9775fa',
      },
      {
        initials: 'NK',
        name: 'NordicKing',
        role: 'Verified Discord Member · Pro since 2024',
        quote: 'Flow Calls 3x a week and mentor feedback on my setups — that combination helped me finally build structure. The community is what sets FlockTrade apart.',
        result: 'Pro member for 12+ months',
        rating: 5,
        avatarColor: '#5865F2',
        accent: '#38bdf8',
      },
      {
        initials: 'TS',
        name: 'TradeSniper_FX',
        role: 'Verified Discord Member · Pro since 2025',
        quote: 'NewsDesk and Sentiment together give me the full picture before I even look at a chart. Saves me hours of scrolling through Twitter and ForexFactory.',
        result: 'Pro member for 4+ months',
        rating: 5,
        avatarColor: '#5865F2',
        accent: '#f472b6',
      },
    ],
  },
};

const HIGHLIGHTS_DEFAULTS = {
  title: 'Stay ahead of every session',
  subtitle: 'Structured planning, sentiment, and accountability rolled into one workspace.',
  cards: [
    {
      key: 'playbooks',
      title: 'Morning playbooks',
      description: 'Get structure, bias, and key levels before London opens.',
      accent: '#38bdf8',
      background: 'linear-gradient(180deg, rgba(17,50,91,0.92) 0%, rgba(8,22,45,0.96) 100%)',
      Icon: ScheduleIcon,
    },
    {
      key: 'flowdata',
      title: 'Real-time flow data',
      description: 'Institutional orderflow, sweep detection, and gamma exposure across major indices and ETFs.',
      accent: '#f7b633',
      background: 'linear-gradient(180deg, rgba(79,56,14,0.92) 0%, rgba(38,24,6,0.96) 100%)',
      Icon: BoltIcon,
    },
    {
      key: 'feedback',
      title: 'Mentor feedback',
      description: 'Drop your trades and get constructive reviews from active pros.',
      accent: '#f472b6',
      background: 'linear-gradient(180deg, rgba(86,26,59,0.92) 0%, rgba(45,11,33,0.96) 100%)',
      Icon: ForumIcon,
    },
  ],
};

const NEWS_SPOTLIGHT_DEFAULTS = {
  title: 'NewsDesk keeps context within reach',
  description: 'Track macro catalysts, index moves, and FX sentiment without bouncing between feeds.',
  bullets: [
    'Instant sentiment scoring on every headline',
    'Smart filters by asset, session, and keyword',
    'Audio recaps for commuting or multitasking',
  ],
  cta: 'Peek at the live NewsDesk',
};

const VALUE_PROPS_DEFAULTS = {
  title: 'Built for consistency',
  items: [
    {
      key: 'insights',
      title: 'Clear journal insights',
      description: 'Review performance with streaks, risk metrics, and trade analytics in one view.',
      accent: '#38bdf8',
    },
    {
      key: 'automation',
      title: 'Automation-ready data feeds',
      description: 'Webhook-friendly flow data for bots, prop platforms, and TradingView integration.',
      accent: '#f7b633',
    },
    {
      key: 'mentorship',
      title: 'Flexible mentorship',
      description: 'Use group calls for accountability or book 1:1 deep dives when you need them.',
      accent: '#f472b6',
    },
  ],
};

const CTA_SECTION_DEFAULTS = {
  title: 'Ready to trade with structure?',
  subtitle: 'Join the community, get the plan, and execute with accountability.',
  primaryLabel: 'Start your membership',
  secondaryLabel: 'Join our Discord floor',
};

const Home = () => {
  const { t } = useTranslation();

  useEffect(() => {
    const script = document.createElement("script");
    script.src = "https://s3.tradingview.com/external-embedding/embed-widget-ticker-tape.js";
    script.async = true;
    script.textContent = JSON.stringify({
      symbols: [
        {
          proName: "BITSTAMP:BTCUSD",
          title: "Bitcoin",
        },
        {
          proName: "BITSTAMP:ETHUSD",
          title: "Ethereum",
        },
        {
          description: "Nas100",
          proName: "TRADENATION:USTEC",
        },
        {
          description: "Dow Jones",
          proName: "TRADENATION:US30",
        },
      ],
      showSymbolLogo: true,
      isTransparent: false,
      displayMode: "adaptive",
      colorTheme: "dark",
      locale: "en",
    });
    const container = document.getElementById("tradingview-ticker-tape");
    if (container) {
      container.appendChild(script);
    }
    script.onerror = () => {
      console.warn('TradingView ticker widget failed to load');
    };
  }, []);

  const membershipPlans = React.useMemo(() => {
    const rawPlans = t('membership.plans', { returnObjects: true, defaultValue: DEFAULT_PLANS });
    return PLAN_ORDER.map((key) => {
      const plan = rawPlans?.[key] || DEFAULT_PLANS[key];
      if (!plan) return null;

      const features = Array.isArray(plan.features) && plan.features.length
        ? plan.features
        : DEFAULT_PLANS[key].features;

      return {
        key,
        name: plan.name || DEFAULT_PLANS[key].name,
        priceAmount: plan.priceAmount || DEFAULT_PLANS[key].priceAmount,
        pricePeriod: plan.pricePeriod || DEFAULT_PLANS[key].pricePeriod,
        features: features.slice(0, 3),
        styles: PLAN_STYLES[key] || PLAN_STYLES.basic,
      };
    }).filter(Boolean);
  }, [t]);

  const reviewShowcase = React.useMemo(() => {
    const localized = t('home.reviewShowcase', { returnObjects: true, defaultValue: {} });

    const howItWorksTitle = localized?.howItWorks?.title || REVIEW_SHOWCASE_DEFAULTS.howItWorks.title;
    const howItWorksSubtitle = localized?.howItWorks?.subtitle || REVIEW_SHOWCASE_DEFAULTS.howItWorks.subtitle;
    const steps = REVIEW_SHOWCASE_DEFAULTS.howItWorks.steps.map((defaultStep, index) => {
      const translatedStep = localized?.howItWorks?.steps?.[index];
      const merged = {
        ...defaultStep,
        ...translatedStep,
      };

      return {
        ...merged,
        Icon: defaultStep.Icon,
        accent: translatedStep?.accent || defaultStep.accent,
        background: translatedStep?.background || defaultStep.background,
      };
    });

    const reviewsTitle = localized?.reviews?.title || REVIEW_SHOWCASE_DEFAULTS.reviews.title;
    const reviewsSubtitle = localized?.reviews?.subtitle || REVIEW_SHOWCASE_DEFAULTS.reviews.subtitle;
    const cards = REVIEW_SHOWCASE_DEFAULTS.reviews.cards.map((defaultCard, index) => {
      const translatedCard = localized?.reviews?.cards?.[index];
      const merged = {
        ...defaultCard,
        ...translatedCard,
      };

      return {
        ...merged,
        avatarColor: translatedCard?.avatarColor || defaultCard.avatarColor,
        accent: translatedCard?.accent || defaultCard.accent,
        rating: translatedCard?.rating ?? defaultCard.rating,
      };
    });

    return {
      howItWorks: {
        title: howItWorksTitle,
        subtitle: howItWorksSubtitle,
        steps,
      },
      reviews: {
        title: reviewsTitle,
        subtitle: reviewsSubtitle,
        cards,
      },
    };
  }, [t]);

  const highlights = React.useMemo(() => {
    const localized = t('home.highlights', { returnObjects: true, defaultValue: {} });
    return {
      title: localized?.title || HIGHLIGHTS_DEFAULTS.title,
      subtitle: localized?.subtitle || HIGHLIGHTS_DEFAULTS.subtitle,
      cards: HIGHLIGHTS_DEFAULTS.cards.map((card, index) => {
        const translation = localized?.cards?.[index];
        return {
          ...card,
          ...translation,
          Icon: card.Icon,
          accent: translation?.accent || card.accent,
          background: translation?.background || card.background,
        };
      }),
    };
  }, [t]);

  const newsSpotlight = React.useMemo(() => {
    const localized = t('home.newsSpotlight', { returnObjects: true, defaultValue: {} });
    const bullets = Array.isArray(localized?.bullets) && localized.bullets.length
      ? localized.bullets
      : NEWS_SPOTLIGHT_DEFAULTS.bullets;

    return {
      title: localized?.title || NEWS_SPOTLIGHT_DEFAULTS.title,
      description: localized?.description || NEWS_SPOTLIGHT_DEFAULTS.description,
      bullets,
      cta: localized?.cta || NEWS_SPOTLIGHT_DEFAULTS.cta,
    };
  }, [t]);

  const valueProps = React.useMemo(() => {
    const localized = t('home.valueProps', { returnObjects: true, defaultValue: {} });
    return {
      title: localized?.title || VALUE_PROPS_DEFAULTS.title,
      items: VALUE_PROPS_DEFAULTS.items.map((item, index) => {
        const translation = localized?.items?.[index];
        return {
          ...item,
          ...translation,
          accent: translation?.accent || item.accent,
        };
      }),
    };
  }, [t]);

  const ctaSection = React.useMemo(() => {
    const title = t('home.cta', { defaultValue: CTA_SECTION_DEFAULTS.title });
    const subtitle = t('home.ctaSubtitle', { defaultValue: CTA_SECTION_DEFAULTS.subtitle });
    const primaryLabel = t('home.joinUs', { defaultValue: CTA_SECTION_DEFAULTS.primaryLabel });
    const secondaryLabel = t('hero.primaryCta', { defaultValue: CTA_SECTION_DEFAULTS.secondaryLabel });

    return { title, subtitle, primaryLabel, secondaryLabel };
  }, [t]);

  return (
    <Box>
      <SEO
        path="/"
        jsonLd={[
          {
            '@context': 'https://schema.org',
            '@type': 'WebSite',
            name: 'FlockTrade',
            url: 'https://flocktrade.com',
            description: 'Data-driven trading tools, education, sentiment analysis and market intelligence platform.',
            potentialAction: {
              '@type': 'SearchAction',
              target: 'https://flocktrade.com/community?q={search_term_string}',
              'query-input': 'required name=search_term_string',
            },
          },
          {
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: [
              { '@type': 'Question', name: 'What markets does FlockTrade cover?', acceptedAnswer: { '@type': 'Answer', text: 'Our flow data and market intelligence covers NAS100, US30, XAUUSD (Gold), major FX pairs, and the full US options market including SPY, QQQ, GLD, and 5000+ tickers.' } },
              { '@type': 'Question', name: 'What trading tools are included?', acceptedAnswer: { '@type': 'Answer', text: 'Real-time options flow, institutional orderflow analysis, gamma exposure mapping, smart money tracking, NewsDesk, sentiment analytics, and a full trade journal.' } },
              { '@type': 'Question', name: 'Can I cancel anytime?', acceptedAnswer: { '@type': 'Answer', text: 'Yes — cancel anytime from your account settings. No lock-in contracts.' } },
              { '@type': 'Question', name: 'What is Option Flow?', acceptedAnswer: { '@type': 'Answer', text: 'Option Flow shows real-time institutional orderflow for stocks and ETFs — block trades, sweeps, and unusual activity that reveals smart money positioning.' } },
              { '@type': 'Question', name: 'How do I access the platform?', acceptedAnswer: { '@type': 'Answer', text: 'Market intelligence and flow data are available in real-time on the FlockTrade platform and our Discord community. You can also integrate with TradingView via webhooks.' } },
              { '@type': 'Question', name: 'Who are the mentors?', acceptedAnswer: { '@type': 'Answer', text: 'Our mentors have 17+ combined years of live market experience. They lead 6 sessions per week covering orderflow, technical analysis, and trade reviews.' } },
            ],
          },
        ]}
      />
      <HeroSection />
      <Box id="tradingview-ticker-tape" sx={{ padding: "0px 0px", mb: 4 }} />

      <Box sx={{ mt: 8, px: { xs: 2, md: 6 }, py: { xs: 6, md: 8 } }}>
        <Stack spacing={2} textAlign="center" maxWidth="md" mx="auto">
          <Typography variant="h4" component="h2" sx={{ fontWeight: 800 }}>
            {highlights.title}
          </Typography>
          <Typography variant="body1" sx={{ color: 'text.secondary' }}>
            {highlights.subtitle}
          </Typography>
        </Stack>
        <Grid container spacing={{ xs: 3, md: 4 }} sx={{ mt: { xs: 4, md: 6 } }}>
          {highlights.cards.map((card) => {
            const Icon = card.Icon;
            return (
              <Grid item xs={12} md={4} key={card.key}>
                <Paper
                  elevation={0}
                  sx={{
                    height: '100%',
                    p: { xs: 3, md: 4 },
                    borderRadius: 4,
                    background: card.background,
                    color: '#fff',
                    border: `1px solid ${card.accent}33`,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                  }}
                >
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: 'rgba(255,255,255,0.12)',
                    }}
                  >
                    <Icon sx={{ color: card.accent, fontSize: 28 }} />
                  </Box>
                  <Typography variant="h6" component="h3" sx={{ fontWeight: 700 }}>
                    {card.title}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.75)' }}>
                    {card.description}
                  </Typography>
                </Paper>
              </Grid>
            );
          })}
        </Grid>
      </Box>

      <Box sx={{ mt: 4, px: { xs: 2, md: 6 } }}>
        <Paper
          elevation={0}
          sx={{
            borderRadius: 4,
            background: 'linear-gradient(135deg, rgba(13,27,54,0.96) 0%, rgba(6,13,28,0.98) 100%)',
            border: '1px solid rgba(56,189,248,0.25)',
            overflow: 'hidden',
          }}
        >
          <Grid container spacing={{ xs: 4, md: 6 }} alignItems="center" sx={{ py: { xs: 4, md: 6 }, px: { xs: 3, md: 6 } }}>
            <Grid item xs={12} md={6}>
              <Stack spacing={2.5}>
                <Typography variant="overline" sx={{ letterSpacing: 2, color: 'rgba(56,189,248,0.8)' }}>
                  NewsDesk
                </Typography>
                <Typography variant="h4" component="h2" sx={{ fontWeight: 800, color: '#fff' }}>
                  {newsSpotlight.title}
                </Typography>
                <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.75)' }}>
                  {newsSpotlight.description}
                </Typography>
                <Stack spacing={1.5}>
                  {newsSpotlight.bullets.map((bullet) => (
                    <Stack key={bullet} direction="row" spacing={1.5} alignItems="flex-start">
                      <CheckCircleOutlineIcon sx={{ color: '#38bdf8', mt: '2px', fontSize: 20 }} />
                      <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                        {bullet}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
                <Button
                  variant="contained"
                  color="primary"
                  sx={{ mt: 1, width: { xs: '100%', sm: 'auto' } }}
                  onClick={() => (window.location.href = '/newsdesk')}
                >
                  {newsSpotlight.cta}
                </Button>
              </Stack>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box
                component="img"
                src={newsDeskImage}
                alt="NewsDesk Screenshot"
                sx={{
                  width: '100%',
                  borderRadius: 3,
                  boxShadow: '0 20px 60px rgba(4,12,28,0.45)',
                  border: '1px solid rgba(255,255,255,0.08)',
                }}
              />
            </Grid>
          </Grid>
        </Paper>
      </Box>

      <AdCard rotationInterval={7000} />

      <Box
        sx={{
          mt: 8,
          px: { xs: 2, md: 6 },
          py: { xs: 6, md: 10 },
          background: 'radial-gradient(circle at top, rgba(12,18,38,0.96), rgba(5,8,20,0.98))',
          color: '#fff',
        }}
      >
        <Box maxWidth="md" mx="auto" textAlign="center" sx={{ mb: { xs: 5, md: 6 } }}>
          <Typography variant="h4" component="h2" sx={{ fontWeight: 800, mb: 1 }}>
            {reviewShowcase.howItWorks.title}
          </Typography>
          <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.7)' }}>
            {reviewShowcase.howItWorks.subtitle}
          </Typography>
        </Box>
        <Grid container spacing={{ xs: 3, md: 4 }}>
          {reviewShowcase.howItWorks.steps.map((step) => {
            const Icon = step.Icon;
            return (
              <Grid item xs={12} md={4} key={`${step.label}-${step.title}`}>
                <Paper
                  elevation={0}
                  sx={{
                    height: '100%',
                    p: { xs: 3, md: 4 },
                    borderRadius: 4,
                    background: step.background,
                    border: '1px solid rgba(255,255,255,0.08)',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                  }}
                >
                  <Typography variant="overline" sx={{ letterSpacing: 2, fontWeight: 700, color: step.accent }}>
                    {step.label}
                  </Typography>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Icon sx={{ color: step.accent, fontSize: 30 }} />
                    <Typography variant="h6" component="h3" sx={{ fontWeight: 700 }}>
                      {step.title}
                    </Typography>
                  </Stack>
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.75)' }}>
                    {step.description}
                  </Typography>
                </Paper>
              </Grid>
            );
          })}
        </Grid>
        <Box maxWidth="md" mx="auto" textAlign="center" sx={{ mt: { xs: 6, md: 8 }, mb: { xs: 4, md: 5 } }}>
          <Typography variant="h4" component="h2" sx={{ fontWeight: 800, mb: 1 }}>
            {reviewShowcase.reviews.title}
          </Typography>
          <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.7)' }}>
            {reviewShowcase.reviews.subtitle}
          </Typography>
        </Box>
        <Grid container spacing={{ xs: 3, md: 4 }}>
          {reviewShowcase.reviews.cards.map((card) => (
            <Grid item xs={12} md={4} key={`${card.name}-${card.role}`}>
              <Paper
                elevation={0}
                sx={{
                  height: '100%',
                  p: { xs: 3, md: 4 },
                  borderRadius: 4,
                  background: 'linear-gradient(180deg, rgba(19,21,40,0.95) 0%, rgba(10,11,25,0.98) 100%)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2.5,
                }}
              >
                <Stack direction="row" spacing={2} alignItems="flex-start">
                  <Avatar sx={{ bgcolor: card.avatarColor, width: 48, height: 48, fontWeight: 700 }}>
                    {card.initials}
                  </Avatar>
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                      {card.name}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)' }}>
                      {card.role}
                    </Typography>
                  </Box>
                  <FormatQuoteRoundedIcon sx={{ color: 'rgba(255,255,255,0.18)', fontSize: 36 }} />
                </Stack>
                <Rating
                  value={card.rating}
                  readOnly
                  precision={0.5}
                  sx={{
                    color: card.accent,
                    '& .MuiRating-iconFilled': { color: card.accent },
                    '& .MuiRating-iconEmpty': { color: 'rgba(255,255,255,0.2)' },
                  }}
                />
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)', fontStyle: 'italic' }}>
                  {card.quote}
                </Typography>
                <Stack
                  direction="row"
                  spacing={1.5}
                  alignItems="center"
                  sx={{
                    mt: 'auto',
                    pt: 2,
                    borderTop: '1px solid rgba(255,255,255,0.08)',
                  }}
                >
                  <CheckCircleIcon sx={{ color: card.accent, fontSize: 20 }} />
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.75)' }}>
                    {card.result}
                  </Typography>
                </Stack>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Box>

      <Box sx={{ mt: 8, px: { xs: 2, md: 6 }, py: { xs: 6, md: 8 } }}>
        <Stack spacing={2} textAlign="center" maxWidth="md" mx="auto">
          <Typography variant="h4" component="h2" sx={{ fontWeight: 800 }}>
            {valueProps.title}
          </Typography>
        </Stack>
        <Grid container spacing={{ xs: 3, md: 4 }} sx={{ mt: { xs: 4, md: 5 } }}>
          {valueProps.items.map((item) => (
            <Grid item xs={12} md={4} key={item.key}>
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 3, md: 4 },
                  height: '100%',
                  borderRadius: 4,
                  backgroundColor: '#0e162f',
                  border: `1px solid ${item.accent}33`,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2,
                }}
              >
                <Box
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: '50%',
                    backgroundColor: `${item.accent}26`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Box sx={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: item.accent }} />
                </Box>
                <Typography variant="h6" component="h3" sx={{ fontWeight: 700, color: '#fff' }}>
                  {item.title}
                </Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                  {item.description}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* FAQ Section */}
      <Box sx={{ mt: 8, px: { xs: 2, md: 6 }, py: { xs: 6, md: 8 } }}>
        <Box maxWidth="md" mx="auto">
          <Typography variant="h4" component="h2" sx={{ fontWeight: 800, mb: 1, textAlign: 'center' }}>
            Frequently Asked Questions
          </Typography>
          <Typography variant="body1" sx={{ color: 'text.secondary', textAlign: 'center', mb: 4 }}>
            Everything you need to know before joining.
          </Typography>
          {[
            { q: 'What markets does FlockTrade cover?', a: 'Our flow data and market intelligence covers NAS100, US30, XAUUSD (Gold), major FX pairs, and the full US options market including SPY, QQQ, GLD, and 5000+ tickers.' },
            { q: 'What trading tools are included?', a: 'Real-time options flow, institutional orderflow analysis, gamma exposure mapping, smart money tracking, NewsDesk, sentiment analytics, and a full trade journal.' },
            { q: 'Can I cancel anytime?', a: 'Yes — cancel anytime from your account settings. No lock-in contracts. If you stay, you keep your current price forever.' },
            { q: 'What is Option Flow?', a: 'Option Flow shows real-time institutional orderflow for stocks and ETFs — block trades, sweeps, and unusual activity that reveals smart money positioning.' },
            { q: 'How do I access the platform?', a: 'Market intelligence and flow data are available in real-time on the FlockTrade platform and our Discord community. You can also integrate with TradingView via webhooks.' },
            { q: 'Who are the mentors?', a: 'Our mentors have 17+ combined years of live market experience. They lead 6 sessions per week covering orderflow, technical analysis, and trade reviews.' },
          ].map((faq) => (
            <Accordion
              key={faq.q}
              disableGutters
              elevation={0}
              sx={{
                backgroundColor: 'rgba(14,22,47,0.7)',
                border: '1px solid rgba(255,255,255,0.08)',
                borderRadius: '12px !important',
                mb: 1.5,
                '&:before': { display: 'none' },
                '&.Mui-expanded': { mb: 1.5 },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon sx={{ color: 'rgba(255,255,255,0.5)' }} />}
                sx={{ px: { xs: 2.5, md: 3 }, py: 0.5 }}
              >
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#fff' }}>
                  {faq.q}
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ px: { xs: 2.5, md: 3 }, pt: 0, pb: 2.5 }}>
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.75)', lineHeight: 1.6 }}>
                  {faq.a}
                </Typography>
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
      </Box>

      <Box
        sx={{
          mt: 8,
          px: 4,
          py: { xs: 6, md: 8 },
          background: 'radial-gradient(circle at top, rgba(23,45,98,0.65), rgba(5,10,25,0.95))',
          color: '#fff',
        }}
      >
        <Box maxWidth="md" mx="auto" textAlign="center" sx={{ mb: 5 }}>
          <Typography variant="h4" component="h2" sx={{ fontWeight: 800, mb: 2 }}>
            {t('home.membershipSection.title')}
          </Typography>
          <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.75)' }}>
            {t('home.membershipSection.subtitle')}
          </Typography>
        </Box>
        <Grid container spacing={4} justifyContent="center">
          {membershipPlans.map((plan) => (
            <Grid item xs={12} sm={6} md={4} key={plan.key}>
              <Paper
                elevation={6}
                sx={{
                  p: 4,
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  borderRadius: 4,
                  background: plan.styles.background,
                  border: `1px solid ${plan.styles.accent}`,
                }}
              >
                <Stack spacing={3}>
                  <Box>
                    <Typography variant="overline" sx={{ letterSpacing: 2, color: plan.styles.accent }}>
                      {plan.name}
                    </Typography>
                    <Stack direction="row" spacing={1} alignItems="baseline">
                      <Typography variant="h4" sx={{ fontWeight: 800 }}>
                        {plan.priceAmount}
                      </Typography>
                      <Typography variant="subtitle2" sx={{ color: 'rgba(255,255,255,0.65)' }}>
                        {plan.pricePeriod}
                      </Typography>
                    </Stack>
                  </Box>

                  <Stack spacing={1.25}>
                    {plan.features.map((feature) => (
                      <Stack key={feature} direction="row" spacing={1.5} alignItems="flex-start">
                        <CheckCircleOutlineIcon sx={{ mt: '2px', color: plan.styles.accent }} />
                        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.85)' }}>
                          {feature}
                        </Typography>
                      </Stack>
                    ))}
                  </Stack>

                  <Button
                    variant="contained"
                    fullWidth
                    sx={{
                      mt: 1,
                      fontWeight: 700,
                      backgroundColor: plan.styles.accent,
                      color: '#0a1225',
                      '&:hover': {
                        backgroundColor: plan.styles.accent,
                        filter: 'brightness(0.9)',
                      },
                    }}
                    onClick={() => (window.location.href = '/membership')}
                  >
                    {t('home.membershipSection.planCta', { plan: plan.name })}
                  </Button>
                </Stack>
              </Paper>
            </Grid>
          ))}
        </Grid>
        <Box textAlign="center" mt={6}>
          <Button
            variant="outlined"
            color="inherit"
            onClick={() => (window.location.href = '/membership')}
            sx={{
              borderColor: 'rgba(255,255,255,0.35)',
              color: '#fff',
              px: 4,
              '&:hover': {
                borderColor: 'rgba(255,255,255,0.6)',
                backgroundColor: 'rgba(255,255,255,0.08)',
              },
            }}
          >
            {t('home.membershipSection.allPlansCta')}
          </Button>
        </Box>
      </Box>

      <Box
        sx={{
          textAlign: 'center',
          px: { xs: 2, md: 4 },
          py: { xs: 6, md: 8 },
        }}
      >
        <Stack spacing={2} maxWidth="md" mx="auto">
          <Typography variant="h4" component="h2" sx={{ fontWeight: 800 }}>
            {ctaSection.title}
          </Typography>
          <Typography variant="body1" sx={{ color: 'text.secondary' }}>
            {ctaSection.subtitle}
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center" sx={{ mt: 2 }}>
            <Button
              variant="contained"
              color="secondary"
              onClick={() => (window.location.href = '/signup')}
            >
              {ctaSection.primaryLabel}
            </Button>
            <Button
              variant="outlined"
              color="secondary"
              onClick={() => window.open('https://discord.com/invite/sQjPufbufK', '_blank')}
            >
              {ctaSection.secondaryLabel}
            </Button>
          </Stack>
        </Stack>
      </Box>
    </Box>
  );
};

export default Home;
